import { useState, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Menu, X, Phone, Mail, MapPin, Instagram, Wine, Grape, Clock, Users } from "lucide-react";

// Image URLs from design guidelines
const IMAGES = {
  hero: "https://images.unsplash.com/photo-1660903293953-e2fd2b2291f3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwyfHx2aW5leWFyZCUyMGxhbmRzY2FwZSUyMHN1bnNldCUyMHJpb2phfGVufDB8fHx8MTc3Mzc5MDExMXww&ixlib=rb-4.1.0&q=85",
  story: "https://images.unsplash.com/photo-1660508477980-ac5307fe39dd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHx2aW5leWFyZCUyMGxhbmRzY2FwZSUyMHN1bnNldCUyMHJpb2phfGVufDB8fHx8MTc3Mzc5MDExMXww&ixlib=rb-4.1.0&q=85",
  wine1: "/images/corazon.png",
  wine2: "/images/crianza.png",
  wine3: "/images/joven.png",
  wine4: "/images/blanco.png",
  cellar: "https://images.unsplash.com/photo-1701395467904-d0d9427bc47a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwxfHx3aW5lJTIwY2VsbGFyJTIwYmFycmVscyUyMHBlcnNwZWN0aXZlfGVufDB8fHx8MTc3Mzc5MDEyOXww&ixlib=rb-4.1.0&q=85",
  grapes: "https://images.unsplash.com/photo-1699045003434-88048b18032b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwyfHxncmFwZXMlMjBvbiUyMHZpbmUlMjBjbG9zZSUyMHVwJTIwc3VubGlnaHR8ZW58MHx8fHwxNzczNzkwMTI3fDA&ixlib=rb-4.1.0&q=85",
  tasting: "https://images.unsplash.com/photo-1713627243577-f1bed80ec393?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHx3aW5lJTIwdGFzdGluZyUyMHBlb3BsZSUyMGVsZWdhbnQlMjBzbWlsaW5nfGVufDB8fHx8MTc3Mzc5MDEyOHww&ixlib=rb-4.1.0&q=85",
};

// Wines data
const WINES = [
  {
    id: 1,
    name: "Corazón",
    type: "Vendimia Seleccionada",
    description: "Vino hecho con uvas elegidas. Es un caldo de fino sabor a la vez que potente y de textura aterciopelada muy equilibrado.",
    grape: "100% Tempranillo",
    image: IMAGES.wine1,
    span: "col-span-12 md:col-span-6 lg:col-span-4",
  },
  {
    id: 2,
    name: "Deme Crianza",
    type: "Crianza",
    description: "Un vino serio y alejado de modas. Vestido sobrio y elegante en copa, con un color intenso y brillante en nariz, aromas de fruta madura con ligero toque de roble.",
    grape: "Tempranillo",
    image: IMAGES.wine2,
    span: "col-span-12 md:col-span-6 lg:col-span-4",
  },
  {
    id: 3,
    name: "Joven",
    type: "Viña Martión",
    description: "Maceración carbónica con uvas enteras. Vino tradicional de la Rioja Alavesa, por su suavidad fácil de beber, invitando a repetir.",
    grape: "85% Tempranillo, 15% Viura",
    image: IMAGES.wine3,
    span: "col-span-12 md:col-span-6 lg:col-span-4",
  },
  {
    id: 4,
    name: "Blanco",
    type: "Viña Martión",
    description: "Un vino blanco vibrante con notas cítricas y florales, recordando la manzana, plátano y una ligera acidez, ideal para acompañar aperitivos.",
    grape: "100% Viura",
    image: IMAGES.wine4,
    span: "col-span-12 md:col-span-6 lg:col-span-12",
    featured: true,
  },
];

// Stats data
const STATS = [
  { number: "1995", label: "Año de fundación" },
  { number: "18", label: "Hectáreas de viñedo" },
  { number: "100+", label: "Años de tradición familiar" },
  { number: "4", label: "Vinos emblemáticos" },
];

// Navigation links
const NAV_LINKS = [
  { href: "#historia", label: "Historia" },
  { href: "#vinos", label: "Vinos" },
  { href: "#viñedos", label: "Viñedos" },
  { href: "#visitas", label: "Visitas" },
  { href: "#contacto", label: "Contacto" },
];

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.8, ease: "easeOut" },
};

const staggerContainer = {
  initial: "hidden",
  whileInView: "visible",
  viewport: { once: true },
  variants: {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 },
    },
  },
};

const staggerItem = {
  variants: {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  },
};

// Components
export const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header
      data-testid="navigation-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "bg-stone-50/95 backdrop-blur-md border-b border-stone-200 py-4" : "bg-transparent py-6"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
        {/* Logo */}
        <a href="#" data-testid="logo-link" className="flex gap-[15px] items-center">
          <img src="/pavoni.png" alt="Logo pavoni" className="w-[90px] h-[90px]" />
          <div className="flex flex-col">
            <span className={`font-serif text-2xl md:text-3xl tracking-tight transition-colors ${isScrolled ? "text-wine-900" : "text-white"}`}>
            Bodegas Pavoni
          </span>
          <span className={`font-script text-sm md:text-base ${isScrolled ? "text-gold-600" : "text-gold-300"}`}>
            Rioja Alavesa
          </span></div>
        </a>

        {/* Desktop Navigation */}
        <ul className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((link) => (
            <li key={link.href}>
              <a
                href={link.href}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
                className={`nav-link font-serif text-lg transition-colors ${
                  isScrolled ? "text-stone-700 hover:text-wine-900" : "text-white/90 hover:text-white"
                }`}
              >
                {link.label}
              </a>
            </li>
          ))}
        </ul>

        {/* Mobile Menu Button */}
        <button
          data-testid="mobile-menu-button"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className={`lg:hidden p-2 transition-colors ${isScrolled ? "text-wine-900" : "text-white"}`}
          aria-label="Menu"
        >
          {isMobileMenuOpen ? <X size={28} strokeWidth={1.5} /> : <Menu size={28} strokeWidth={1.5} />}
        </button>
      </nav>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          data-testid="mobile-menu"
          className="lg:hidden absolute top-full left-0 right-0 bg-stone-50 border-b border-stone-200 shadow-lg"
        >
          <ul className="flex flex-col py-6 px-6">
            {NAV_LINKS.map((link) => (
              <li key={link.href}>
                <a
                  href={link.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block py-3 font-serif text-xl text-stone-700 hover:text-wine-900 transition-colors"
                >
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
        </motion.div>
      )}
    </header>
  );
};

export const HeroSection = () => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 500], [0, 150]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0]);

  return (
    <section id="hero" data-testid="hero-section" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image with Parallax */}
      <motion.div style={{ y }} className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt="Vinedos de Rioja Alavesa al atardecer"
          className="w-full h-full object-cover warm-filter"
        />
        <div className="absolute inset-0 overlay-gradient" />
      </motion.div>

      {/* Content */}
      <motion.div style={{ opacity }} className="relative z-10 text-center px-6 max-w-5xl mx-auto">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="font-script text-2xl md:text-3xl text-gold-300 mb-4"
        >
          Desde 1995
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="font-serif text-5xl md:text-7xl lg:text-8xl text-white tracking-tight hero-text-shadow mb-6"
        >
          Bodegas Pavoni
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="text-lg md:text-xl text-white/90 max-w-2xl mx-auto mb-10 leading-relaxed"
        >
          Bodega familiar con raíces profundas en la cultura vinicola de la Rioja Alavesa. 
          Más de un siglo dedicados a la viticultura y al arte de elaborar vinos excepcionales.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="flex flex-col sm:flex-row gap-4 justify-center"
        >
          <a href="#vinos" data-testid="hero-cta-vinos" className="btn-primary inline-block">
            Descubre Nuestros Vinos
          </a>
          <a href="#visitas" data-testid="hero-cta-visitas" className="inline-block px-8 py-4 uppercase tracking-widest text-xs border border-white text-white hover:bg-white hover:text-wine-900 transition-colors duration-300">
            Visita la Bodega
          </a>
        </motion.div>
      </motion.div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 1.5 }}
          className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center pt-2"
        >
          <motion.div className="w-1.5 h-1.5 bg-white rounded-full" />
        </motion.div>
      </motion.div>
    </section>
  );
};

export const StatsSection = () => {
  return (
    <section data-testid="stats-section" className="bg-wine-900 py-16 md:py-20">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          {...staggerContainer}
          className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12"
        >
          {STATS.map((stat, index) => (
            <motion.div key={index} {...staggerItem} className="text-center">
              <p className="stat-number text-4xl md:text-5xl lg:text-6xl text-gold-500 mb-2">
                {stat.number}
              </p>
              <p className="text-sm md:text-base uppercase tracking-widest text-stone-300">
                {stat.label}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export const HistorySection = () => {
  return (
    <section id="historia" data-testid="history-section" className="py-24 md:py-32 bg-stone-50">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image */}
          <motion.div {...fadeInUp} className="relative">
            <div className="aspect-[4/5] overflow-hidden">
              <img
                src={IMAGES.cellar}
                alt="Bodega con barricas de roble"
                className="w-full h-full object-cover warm-filter"
              />
            </div>
            {/* Decorative element */}
            <div className="absolute -bottom-6 -right-6 w-32 h-32 border-2 border-gold-500 -z-10" />
          </motion.div>

          {/* Content */}
          <motion.div {...fadeInUp} className="lg:pl-8">
            <p className="text-sm uppercase tracking-[0.2em] text-gold-600 mb-4">Nuestra Historia</p>
            <h2 className="font-serif text-4xl md:text-5xl text-stone-800 tracking-tight mb-8">
              Tradición y Pasión por el Vino
            </h2>
            <div className="space-y-6 text-stone-600 leading-relaxed">
              <p>
                Bodegas Pavoni es una bodega familiar con raíces profundas en la cultura vinicola de la Rioja Alavesa. 
                Desde hace generaciones, nuestra familia ha dedicado su vida a la producción de vinos, 
                respetando las tradiciones y abrazando la innovación.
              </p>
              <p>
                Nuestra bodega, ubicada en un edificio del siglo XVII, fue renovada en los años 1980 y 1991, 
                respetando las zonas antiguas incluyendo las cuevas y calados tradicionales que dan carácter 
                único a nuestros vinos.
              </p>
              <p>
                Lapuebla de Labarca, en la frontera de Rioja Alavesa y La Rioja, separada por el rio Ebro, 
                es un lugar idóneo para el cultivo de la vida, donde combinamos métodos tradicionales con 
                tecnología moderna.
              </p>
            </div>
            <div className="mt-10 pt-10 border-t border-stone-200 flex items-center gap-8">
              <div>
                <p className="font-serif text-3xl text-wine-900">Siglo XVII</p>
                <p className="text-sm text-stone-500 uppercase tracking-wider">Bodega Original</p>
              </div>
              <div className="w-px h-12 bg-gold-500" />
              <div>
                <p className="font-serif text-3xl text-wine-900">1995</p>
                <p className="text-sm text-stone-500 uppercase tracking-wider">Fundación</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export const WinesSection = () => {
  return (
    <section id="vinos" data-testid="wines-section" className="py-24 md:py-32 bg-stone-100 grain-overlay">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div {...fadeInUp} className="text-center mb-16 md:mb-20">
          <p className="text-sm uppercase tracking-[0.2em] text-gold-600 mb-4">Nuestra Colección</p>
          <h2 className="font-serif text-4xl md:text-5xl text-stone-800 tracking-tight mb-6">
            Vinos con Carácter
          </h2>
          <p className="text-stone-600 max-w-2xl mx-auto">
            Cada botella cuenta la historia de nuestra tierra, elaborada con uvas de nuestros 18 hectáreas 
            de viñedo entre Lapuebla de Labarca y Laguardia.
          </p>
        </motion.div>

        {/* Wine Grid */}
        <motion.div {...staggerContainer} className="bento-grid">
          {WINES.map((wine) => (
            <motion.div
              key={wine.id}
              {...staggerItem}
              className={`wine-card bg-white p-6 md:p-8 border border-stone-200 ${wine.span}`}
              data-testid={`wine-card-${wine.id}`}
            >
              <div className={`${wine.featured ? "grid md:grid-cols-2 gap-8 items-center" : ""}`}>
                <div className={`aspect-[3.65/4] overflow-hidden mb-6 ${wine.featured ? "md:mb-0" : ""}`}>
                  <img
                    src={wine.image}
                    alt={wine.name}
                    className="wine-image w-full h-full object-none warm-filter"
                  />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-gold-600 mb-2">{wine.type}</p>
                  <h3 className="font-serif text-2xl md:text-3xl text-stone-800 mb-3">{wine.name}</h3>
                  <p className="text-stone-600 mb-4 leading-relaxed">{wine.description}</p>
                  <div className="flex items-center gap-2 text-sm text-stone-500">
                    <Grape size={16} strokeWidth={1.5} className="text-wine-700" />
                    <span>{wine.grape}</span>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export const VineyardsSection = () => {
  return (
    <section id="viñedos" data-testid="vineyards-section" className="relative py-24 md:py-32 overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src={IMAGES.grapes}
          alt="Uvas en la vid bajo la luz del sol"
          className="w-full h-full object-cover warm-filter"
        />
        <div className="absolute inset-0 bg-stone-900/70" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        <motion.div {...fadeInUp} className="max-w-3xl">
          <p className="text-sm uppercase tracking-[0.2em] text-gold-400 mb-4">Nuestros Viñedos</p>
          <h2 className="font-serif text-4xl md:text-5xl text-white tracking-tight mb-8">
            18 Hectáreas de Excelencia
          </h2>
          <p className="text-lg text-stone-200 leading-relaxed mb-8">
            Nuestros viñedos se extienden entre Lapuebla de Labarca y Laguardia, en terrenos privilegiados 
            de la Rioja Alavesa. 15 hectáreas cultivadas en vaso tradicional y 3 hectáreas en espaldera, 
            siguiendo métodos que respetan la tradición mientras incorporan las mejores prácticas modernas.
          </p>
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="bg-white/10 backdrop-blur-sm p-6 border border-white/20">
              <Wine size={32} strokeWidth={1.5} className="text-gold-400 mb-4" />
              <h3 className="font-serif text-xl text-white mb-2">15 Ha en Vaso</h3>
              <p className="text-stone-300 text-sm">Método tradicional que produce uvas de alta concentración</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm p-6 border border-white/20">
              <Grape size={32} strokeWidth={1.5} className="text-gold-400 mb-4" />
              <h3 className="font-serif text-xl text-white mb-2">3 Ha en Espaldera</h3>
              <p className="text-stone-300 text-sm">Sistema moderno para óptima maduración de la uva</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export const VisitsSection = () => {
  return (
    <section id="visitas" data-testid="visits-section" className="py-24 md:py-32 bg-stone-50">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Content */}
          <motion.div {...fadeInUp}>
            <p className="text-sm uppercase tracking-[0.2em] text-gold-600 mb-4">Enoturismo</p>
            <h2 className="font-serif text-4xl md:text-5xl text-stone-800 tracking-tight mb-8">
              Visita Nuestra Bodega
            </h2>
            <p className="text-stone-600 leading-relaxed mb-8">
              Te invitamos a descubrir los secretos de nuestra bodega del siglo XVII y disfrutar 
              de una experiencia única de enoturismo en la Rioja Alavesa. Pasea entre nuestras barricas, 
              conoce el proceso de elaboración y finaliza con una cata de nuestros mejores vinos.
            </p>
            
            <div className="space-y-6 mb-10">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-wine-100 flex items-center justify-center flex-shrink-0">
                  <Clock size={24} strokeWidth={1.5} className="text-wine-900" />
                </div>
                <div>
                  <h3 className="font-serif text-lg text-stone-800 mb-1">Visita Guiada</h3>
                  <p className="text-stone-600 text-sm">Recorrido por la bodega histórica, depositos y sala de barricas</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-wine-100 flex items-center justify-center flex-shrink-0">
                  <Wine size={24} strokeWidth={1.5} className="text-wine-900" />
                </div>
                <div>
                  <h3 className="font-serif text-lg text-stone-800 mb-1">Cata de Vinos</h3>
                  <p className="text-stone-600 text-sm">Degustación de nuestra selección de vinos D.O.Ca. Rioja</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-wine-100 flex items-center justify-center flex-shrink-0">
                  <Users size={24} strokeWidth={1.5} className="text-wine-900" />
                </div>
                <div>
                  <h3 className="font-serif text-lg text-stone-800 mb-1">Grupos Reducidos</h3>
                  <p className="text-stone-600 text-sm">Atención personalizada para una experiencia inolvidable</p>
                </div>
              </div>
            </div>

            <p className="text-stone-700 font-medium mb-4">Para reservar tu visita:</p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a 
                href="tel:609905871" 
                data-testid="visits-phone-link"
                className="btn-primary inline-flex items-center justify-center gap-2"
              >
                <Phone size={18} strokeWidth={1.5} />
                609 905 871
              </a>
              <a 
                href="mailto:juancarlos@bodegaspavoni.com"
                data-testid="visits-email-link"
                className="btn-secondary inline-flex items-center justify-center gap-2"
              >
                <Mail size={18} strokeWidth={1.5} />
                Enviar Email
              </a>
            </div>
          </motion.div>

          {/* Image */}
          <motion.div {...fadeInUp} className="relative">
            <div className="aspect-[4/5] overflow-hidden">
              <img
                src={IMAGES.tasting}
                alt="Cata de vinos en Bodegas Pavoni"
                className="w-full h-full object-cover warm-filter"
              />
            </div>
            {/* Decorative element */}
            <div className="absolute -top-6 -left-6 w-32 h-32 border-2 border-gold-500 -z-10" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export const LocationSection = () => {
  return (
    <section id="localizacion" data-testid="location-section" className="py-24 md:py-32 bg-stone-100">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div {...fadeInUp} className="text-center mb-12">
          <p className="text-sm uppercase tracking-[0.2em] text-gold-600 mb-4">Donde Estamos</p>
          <h2 className="font-serif text-4xl md:text-5xl text-stone-800 tracking-tight mb-6">
            Localización
          </h2>
          <p className="text-stone-600 max-w-2xl mx-auto">
            Situados en el corazón de la Rioja Alavesa, en Lapuebla de Labarca, 
            a orillas del rio Ebro.
          </p>
        </motion.div>

        <motion.div {...fadeInUp} className="grid lg:grid-cols-3 gap-8">
          {/* Map */}
          <div className="lg:col-span-2">
            <div className="map-container border border-stone-200">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2932.8741507447387!2d-2.5726!3d42.5489!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xd4fc6e2fb7d9ef9%3A0x9c0e9cd21c6d5c8b!2sLapuebla%20de%20Labarca%2C%20%C3%81lava!5e0!3m2!1ses!2ses!4v1699000000000!5m2!1ses!2ses"
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Ubicación de Bodegas Pavoni"
              />
            </div>
          </div>

          {/* Address Info */}
          <div className="bg-white p-8 md:p-10 border border-stone-200">
            <h3 className="font-serif text-2xl text-stone-800 mb-6">Bodegas Pavoni</h3>
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <MapPin size={24} strokeWidth={1.5} className="text-wine-900 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-stone-800 font-medium">Dirección</p>
                  <p className="text-stone-600">
                    Bodegas 41<br />
                    01306 Lapuebla de Labarca<br />
                    Alava, Pais Vasco
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Phone size={24} strokeWidth={1.5} className="text-wine-900 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-stone-800 font-medium">Teléfono</p>
                  <a href="tel:609905871" className="text-wine-900 hover:text-wine-700 transition-colors">
                    609 905 871
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Mail size={24} strokeWidth={1.5} className="text-wine-900 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-stone-800 font-medium">Email</p>
                  <a href="mailto:juancarlos@bodegaspavoni.com" className="text-wine-900 hover:text-wine-700 transition-colors break-all">
                    juancarlos@bodegaspavoni.com
                  </a>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export const ContactSection = () => {
  return (
    <section id="contacto" data-testid="contact-section" className="py-24 md:py-32 bg-stone-50">
      <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
        <motion.div {...fadeInUp}>
          <p className="text-sm uppercase tracking-[0.2em] text-gold-600 mb-4">Contacto</p>
          <h2 className="font-serif text-4xl md:text-5xl text-stone-800 tracking-tight mb-8">
            Hablemos
          </h2>
          <p className="text-stone-600 mb-12 max-w-2xl mx-auto">
            Estamos a tu disposición para cualquier consulta sobre nuestros vinos, 
            visitas a la bodega o colaboraciones comerciales.
          </p>

          {/* Large Contact Info */}
          <div className="space-y-8 mb-12">
            <div>
              <p className="text-sm uppercase tracking-[0.2em] text-stone-500 mb-2">Teléfono</p>
              <a
                href="tel:609905871"
                data-testid="contact-phone"
                className="font-serif text-3xl md:text-4xl text-wine-900 hover:text-wine-700 transition-colors contact-link"
              >
                609 905 871
              </a>
            </div>
            <div>
              <p className="text-sm uppercase tracking-[0.2em] text-stone-500 mb-2">Email</p>
              <a
                href="mailto:juancarlos@bodegaspavoni.com"
                data-testid="contact-email"
                className="font-serif text-2xl md:text-3xl text-wine-900 hover:text-wine-700 transition-colors contact-link break-all"
              >
                juancarlos@bodegaspavoni.com
              </a>
            </div>
          </div>

          <div className="section-divider mx-auto mb-12" />

          <p className="text-stone-600">
            <span className="font-medium text-stone-800">Juan Carlos</span> te atenderá personalmente
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer data-testid="footer" className="bg-stone-900 text-stone-200 py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* Brand */}
          <div className="lg:col-span-2">
            <h3 className="font-serif text-3xl text-white mb-2">Bodegas Pavoni</h3>
            <p className="font-script text-xl text-gold-500 mb-6">Rioja Alavesa</p>
            <p className="text-stone-400 leading-relaxed max-w-md">
              Bodega familiar con más de un siglo de tradición vinicola. 
              Elaboramos vinos D.O.Ca. Rioja con pasin y dedicación.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-sm uppercase tracking-[0.2em] text-gold-500 mb-6">Enlaces</h4>
            <ul className="space-y-3">
              {NAV_LINKS.map((link) => (
                <li key={link.href}>
                  <a href={link.href} className="text-stone-400 hover:text-white transition-colors">
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-sm uppercase tracking-[0.2em] text-gold-500 mb-6">Contacto</h4>
            <ul className="space-y-3 text-stone-400">
              <li>Bodegas 41</li>
              <li>01306 Lapuebla de Labarca</li>
              <li>Alava, Pais Vasco</li>
              <li className="pt-2">
                <a href="tel:609905871" className="hover:text-white transition-colors">609 905 871</a>
              </li>
              <li>
                <a href="mailto:juancarlos@bodegaspavoni.com" className="hover:text-white transition-colors break-all">
                  juancarlos@bodegaspavoni.com
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="pt-8 border-t border-stone-800 flex flex-col md:flex-row items-center justify-between gap-6">
          <p className="text-stone-500 text-sm">
            &copy; {currentYear} Bodegas Pavoni. Todos los derechos reservados.
          </p>
          <div className="flex items-center gap-6">
            <a
              href="https://www.instagram.com/bodegaspavoni"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-instagram"
              className="text-stone-400 hover:text-gold-500 transition-colors"
              aria-label="Instagram"
            >
              <Instagram size={24} strokeWidth={1.5} />
            </a>
            <a
              href="https://bodegaspavoni.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-website"
              className="text-stone-400 hover:text-gold-500 transition-colors text-sm"
            >
              bodegaspavoni.com
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main Landing Page Component
const LandingPage = () => {
  return (
    <main>
      <Navigation />
      <HeroSection />
      <StatsSection />
      <HistorySection />
      <WinesSection />
      <VineyardsSection />
      <VisitsSection />
      <LocationSection />
      <ContactSection />
      <Footer />
    </main>
  );
};

export default LandingPage;
